import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { generateUuid } from '../lib/uuid.js'

/**
 * Replacement for the legacy `IntelligentWebContext`. The legacy
 * provider managed:
 *   - `ometricsAIId` and `aiType`
 *   - `sessionId` (regenerated per page-load)
 *   - `activeCtas` and `activeLeadForms`
 *   - `currentPage` (kept in sync with `window.location` and SPA route
 *     changes so RAG context stays accurate)
 *   - the merged `conf` object that all rendering code reads from
 *
 * We mirror that surface but drop the imperative `window.OmetricsAI_*`
 * lookups - the v2 widget is mounted from `widget.js`, which already
 * passed the per-instance config object in.
 */
const IntelligentWebContext = createContext(null)

export function IntelligentWebProvider({ value: initialValue, children }) {
  const [conf, setConfState] = useState(initialValue.conf)
  const [sessionId] = useState(initialValue.conf?.sessionId || generateUuid())
  const [currentPage, setCurrentPage] = useState(
    typeof window !== 'undefined' ? window.location.href : '',
  )
  const [activeCtas, setActiveCtas] = useState(
    initialValue.conf?.activeCtas ?? [],
  )
  const [activeLeadForms, setActiveLeadForms] = useState(
    initialValue.conf?.activeLeadForms ?? [],
  )

  const setConf = useCallback((next) => {
    setConfState((prev) =>
      typeof next === 'function'
        ? next(prev)
        : { ...prev, ...next, sessionId: prev.sessionId || sessionId },
    )
  }, [sessionId])

  const fetchLeadFormById = useCallback(
    (id) => activeLeadForms.find((f) => String(f.id) === String(id)) || null,
    [activeLeadForms],
  )

  const value = useMemo(
    () => ({
      conf,
      setConf,
      sessionId,
      currentPage,
      setCurrentPage,
      activeCtas,
      setActiveCtas,
      activeLeadForms,
      setActiveLeadForms,
      fetchLeadFormById,
      ometricsAIId: conf?.chatId || conf?.agentId || '',
      aiType: conf?.type || 'web',
      isWebAi: Boolean(conf?.isWebAi),
      instanceId: initialValue.instanceId,
      apiBaseUrl: initialValue.apiBaseUrl,
    }),
    [
      conf,
      setConf,
      sessionId,
      currentPage,
      activeCtas,
      activeLeadForms,
      fetchLeadFormById,
      initialValue.instanceId,
      initialValue.apiBaseUrl,
    ],
  )

  return (
    <IntelligentWebContext.Provider value={value}>
      {children}
    </IntelligentWebContext.Provider>
  )
}

export function useIntelligentWeb() {
  const ctx = useContext(IntelligentWebContext)
  if (!ctx) {
    throw new Error(
      'useIntelligentWeb must be used inside <IntelligentWebProvider>',
    )
  }
  return ctx
}
